import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { formValueSelector } from 'redux-form';
import { Redirect } from 'react-router';
import {
  fetchStudentCreate,
  REQUEST_STUDENT_CREATE,
  ROLE,
} from '@/modules/User';
import Centrify from 'sama_global/components/Centrify';
import { Box } from 'UIKit/Grid';
import Panel from 'sama_global/components/Panel';
import { useSnackbarListener } from 'sama_global/utils/hooks/snackbar';
import Form from 'sama_global/components/Form';
import {
  email as e,
  required,
  minLength4,
  validDate,
  universalPhoneNumber,
} from '@/utils/Validate';
import RenderDateField from 'sama_global/components/RenderInput/RenderDateField';
import RenderSelectField from 'sama_global/components/RenderInput/RenderSelectField';
import {
  fetchAddParticipation,
  REQUEST_ADD_PARTICIPATION,
} from '@/modules/Olimpic';
import {
  countries,
  useCategories,
  fetchDelegates,
  REQUEST_GET_DELEGATES,
} from 'sama_global/modules/olimpic';
import { isAdmin } from 'sama_global/utils/protect';

const selector = formValueSelector('olimpic_participants_create');

const Register = ({
  createUser,
  requests,
  participate,
  olimpic,
  birthday: birth,
  loadDelegates,
  user,
}) => {
  const schoolsRequest = requests[REQUEST_GET_DELEGATES];

  const delegates = useMemo(
    () =>
      schoolsRequest?.data?.map(delegate => ({
        value: delegate.id,
        label: `${delegate.name_en} ${delegate.surname_en}`,
      })),
    [schoolsRequest]
  );
  const fields = [
    {
      name: 'email',
      locale: 'EMAIL',
      validate: [required, e],
    },
    {
      name: 'password',
      locale: 'PASSWORD',
      validate: [required, minLength4],
    },
    {
      name: 'name_en',
      locale: 'NAME',
      validate: [required],
    },
    {
      name: 'surname_en',
      locale: 'SURNAME',
      validate: [required],
    },
    {
      name: 'birthday',
      locale: 'DATEBIRTH',
      validate: [validDate],
      component: RenderDateField,
    },
    {
      name: 'phone',
      locale: 'PHONE',
      validate: [universalPhoneNumber],
    },
    {
      name: 'category',
      locale: 'SELECT_CATEGORY',
      validate: [required],
      component: RenderSelectField,
      options: useCategories({ birthday: birth, role: ROLE.LEARNER }),
    },
    {
      name: 'country',
      locale: 'SELECT_COUNTRY',
      validate: [required],
      component: RenderSelectField,
      options: countries,
    },
  ];

  if (isAdmin({ user }))
    fields.unshift({
      /* eslint-disable camelcase */
      name: 'creator_id',
      locale: 'DELEGATE',
      validate: [required],
      options: delegates || [],
      component: RenderSelectField,
    });

  const request = requests[REQUEST_ADD_PARTICIPATION];
  const requestUser = requests[REQUEST_STUDENT_CREATE];

  const handleSubmit = async formData => {
    const { category, country, creator_id: crid } = formData;

    const response = await createUser({
      ...formData,
      meta: { smgl: true },
      creator_id: { value: crid || user._id },
      lang: 'en',
      purchases: {
        access: 0,
        olimpic_3: 1,
      },
    });

    if (!response.error) {
      participate(olimpic?._id, {
        ids: [response.payload?.data?._id],
        params: { category, country },
      });
    }
  };

  useEffect(() => {
    if (olimpic?._id) loadDelegates(olimpic?._id);
  }, [olimpic]);

  useSnackbarListener(requestUser?.errorMessage);
  useSnackbarListener(request?.errorMessage);

  return (
    <Centrify>
      <Box py={5} w={1} style={{ maxWidth: '620px', margin: 'auto' }}>
        <Panel>
          {request?.success && <Redirect to="/olimpic/participants" />}
          <Form
            title="CREATE_PARTICIPANT"
            fields={fields}
            onSubmit={handleSubmit}
            fit
            form="olimpic_participants_create"
          />
        </Panel>
      </Box>
    </Centrify>
  );
};

Register.propTypes = {
  requests: PropTypes.object.isRequired,
  loadDelegates: PropTypes.func.isRequired,
  createUser: PropTypes.func.isRequired,
  participate: PropTypes.func.isRequired,
  olimpic: PropTypes.object.isRequired,
  birthday: PropTypes.string.isRequired,
  user: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
  requests: state.requests,
  olimpic: state.olimpic.sama || {},
  birthday: selector(state, 'birthday'),
  user: state.user,
});

const mapDispatchToProps = dispatch => ({
  createUser(formData) {
    return dispatch(fetchStudentCreate(formData));
  },
  participate(id, formData) {
    dispatch(fetchAddParticipation(formData, id));
  },
  loadDelegates(id) {
    dispatch(fetchDelegates(id));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(Register);
